/**
 * Takes an object and returns the key names as an array without Typescript errors.
 * @param obj The object with keys you want to return
 */
function getKeys<T extends {}>(obj: T): (keyof T)[] {
  return Object.keys(obj) as (keyof T)[];
}

/**
 * Takes an object and returns the entries of Object names as an array without Typescript errors.
 * @param obj The object with keys you want to return
 */
function getEntries<T extends {}>(obj: T): [keyof T, T[keyof T]][] {
  return Object.entries(obj) as [keyof T, T[keyof T]][];
}

/**
 * Formats query params to be used in Elastic query
 * @param params Record<string, any>
 * @returns
 */
function formatQueryParams(params: Record<string, any>) {
  const query = Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => {
      if (typeof value === 'object') {
        value = JSON.stringify(value);
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
    .join('&');
  return query;
}

/**
 * It gives the first letter of 2 words(separated by space). Ex: John Mill then it gives JM
 * @param name string
 * @returns string
 */
const getInitialLetters = (name: string) => {
  if (!name) return '#';
  const splitName = name.split(' ');
  if (splitName.length === 1) return splitName[0].charAt(0);
  const firstNameInitial = splitName[0].charAt(0);
  const lastNameInitial = splitName[1].charAt(0);
  return `${firstNameInitial}${lastNameInitial}`;
};

/**
 * Clean the object - removed undefined/null/empty key-value
 * @param obj Object
 * @returns cleaned object Object
 */
const cleanse = (obj: Record<string, any> | undefined) => {
  if (!obj) return undefined;
  const cleaned = Object.entries(obj).reduce((acc, [key, value]) => {
    if (value) {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, any>);
  return Object.keys(cleaned).length ? cleaned : undefined;
};

export const ObjectHelper = {
  getKeys,
  getEntries,
  formatQueryParams,
  getInitialLetters,
  cleanse,
};
